import React, { useState, useEffect } from 'react';
import { useAuth } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import { apiService, Device, HealthStatus } from './services/api';
import {
  resolveTabId,
  PAGE_HEADER_TITLES,
  UEM_NAV_ITEMS,
  isPlatformNavTab,
  PLATFORM_NAV_ITEMS,
  GOVERNANCE_PATH_TO_VIEW,
  isPoliciesComplianceTab,
  canAccessPlatformPage,
  NAV_MODULE_BY_ID,
  isPlatformModuleEnabled,
} from './config/navigation';
import { PageHeaderProvider } from './contexts/PageHeaderContext';
import { UEMSidebar } from './components/UEMSidebar';
import BuntuDashboard from './components/BuntuDashboard';
import BuntuCompanion from './components/BuntuCompanion';
import ApplicationDeployment from './components/ApplicationDeployment';
import RemoteExecutionHub from './components/RemoteExecutionHub';
import PatchManagement from './components/PatchManagement';
import DevicesHub from './components/DevicesHub';
import InventoryHub from './components/InventoryHub';
import AnalyticsHub from './components/AnalyticsHub';
import SettingsHub from './components/SettingsHub';
import BuntuCompanionHub from './components/BuntuCompanionHub';
import LicenseManagement from './components/LicenseManagement';
import AssetManagementHub from './components/assets/AssetManagementHub';
import SoftwareMeteringHub from './components/software-metering/SoftwareMeteringHub';
import PoliciesComplianceHub from './components/governance/PoliciesComplianceHub';
import UserManagement from './components/UserManagement';
import PlatformAdminClients from './components/PlatformAdminClients';
import ClientAdminConsole from './components/ClientAdminConsole';
import ModuleNoAccess from './components/ModuleNoAccess';
import {
  PlatformAdminDashboard,
  PlatformSubscriptionsPage,
  PlatformBillingPage,
  PlatformLicenseUsagePage,
  PlatformEntitlementsPage,
  PlatformSoftwareMeteringPage,
  PlatformUsersPage,
  PlatformAuditLogsPage,
  PlatformSystemHealthPage,
  PlatformSettingsPage,
} from './components/platform/PlatformPages';
import PlatformBuntuBroadcasts from './components/platform/PlatformBuntuBroadcasts';
import './App.css';
import './styles/hub-shell.css';

function App() {
  const { isAuthenticated, isLoading, user, logout, isPlatformStaff, isPlatformAdmin, portalTenantId, enterClientPortal, exitClientPortal, portalTenantName, refreshAccess, access, accessError } = useAuth();
  const [tab, setTab] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem('uem_user') || 'null');
      const role = String(u?.role || '').toUpperCase();
      const staff = !!(u?.is_platform_staff || u?.is_platform_admin || role.startsWith('PLATFORM_'));
      const portal = localStorage.getItem('platform_active_tenant_id');
      // Command center: never let leftover /policies|/compliance paths steal the landing tab.
      if (staff && !portal) return 'platform-dashboard';

      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/assets')) {
        return 'asset-management';
      }
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/software-metering')) {
        return 'software-metering';
      }
      if (typeof window !== 'undefined') {
        const p = window.location.pathname.replace(/\/+$/, '') || '/';
        if (GOVERNANCE_PATH_TO_VIEW[p] || p.startsWith('/policies/') || p.startsWith('/compliance/')) {
          return 'policies-compliance';
        }
      }
    } catch {
      /* ignore */
    }
    return 'overview';
  });
  const [settingsInitialTab, setSettingsInitialTab] = useState<string | null>(null);
  const [selectedOU, setSelectedOU] = useState<string | null>(null);
  const [viewingDirectory, setViewingDirectory] = useState(false);
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('uem_sidebar_collapsed') === '1';
    } catch {
      return false;
    }
  });

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('uem_sidebar_collapsed', next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  useEffect(() => {
    if (settingsInitialTab !== 'buntu') return;
    setTab('buntu-companion');
    setSettingsInitialTab(null);
  }, [settingsInitialTab]);

  const role = String(user?.role || '').toUpperCase();
  const isClientAdmin = isPlatformAdmin || ['CLIENT_ADMIN', 'CLIENT_MANAGER', 'CLIENT_VIEWER', 'DEVICE_USER'].includes(role);
  const inCommandCenter = isPlatformStaff && !portalTenantId;

  // Platform staff without portal: keep them on allowed platform pages only
  useEffect(() => {
    if (!isAuthenticated || isLoading) return;
    if (!inCommandCenter) return;
    const resolved = resolveTabId(tab);
    if (!isPlatformNavTab(resolved) && resolved !== 'platform-admin') {
      setTab('platform-dashboard');
      return;
    }
    if (!canAccessPlatformPage(resolved, role)) {
      setTab('platform-dashboard');
    }
  }, [isAuthenticated, isLoading, inCommandCenter, tab, role]);

  const openClientPortal = async (tenantId: string, tenantName: string) => {
    enterClientPortal(tenantId, tenantName);
    try {
      await refreshAccess();
    } catch {
      /* server still enforces */
    }
    setTab('overview');
  };
  const [devices, setDevices] = useState<Device[]>([]);
  const [complianceData, setComplianceData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Check API health (gracefully handle backend unavailable)
      const health = await apiService.checkHealth();
      setHealthStatus(health);

      // Load devices (gracefully handle backend unavailable)
      const devicesData = await apiService.getDevices();
      setDevices(devicesData || []);

      // Load compliance data (gracefully handle backend unavailable)
      const complianceData = await apiService.getDeviceCompliance();
      setComplianceData(complianceData || []);

    } catch (err) {
      // Only set error for unexpected errors, not connection refused
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      if (!errorMessage.includes('Failed to fetch') && !errorMessage.includes('ERR_CONNECTION_REFUSED')) {
        setError(errorMessage);
      }
      // Ensure default empty states if backend is unavailable
      // (The API service already returns empty arrays/null, so this is just a safety check)
    } finally {
      setLoading(false);
    }
  };

  const LoadingSpinner = () => (
    <div className="loading">
      <div className="loading-spinner"></div>
      <span style={{ marginLeft: '8px', color: '#6b7280' }}>Loading...</span>
    </div>
  );

  const ErrorMessage = ({ message, onRetry }: { message: string; onRetry?: () => void }) => (
    <div className="error-message">
      <svg className="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <div className="error-content">
        <p className="error-title">API Connection Error</p>
        <p className="error-text">{message}</p>
        {onRetry && (
          <button className="retry-btn" onClick={onRetry}>
            Retry
          </button>
        )}
      </div>
    </div>
  );

  const Overview = () => {
    const totalDevices = devices.length;
    const compliantDevices = complianceData.filter(d => d.compliance_status === 'compliant').length;
    const compliancePercentage = totalDevices > 0 ? Math.round((compliantDevices / totalDevices) * 100) : 0;
    const nonCompliantDevices = totalDevices - compliantDevices;

    // Count devices by OS (supports Windows, Linux, macOS, Android, iOS)
    const osCounts = devices.reduce((acc, device) => {
      const os = device.os?.toLowerCase() || 'unknown';
      // Normalize OS names
      let normalizedOS = os;
      if (os.includes('windows')) normalizedOS = 'windows';
      else if (os.includes('linux') || os.includes('ubuntu') || os.includes('debian') || os.includes('centos') || os.includes('rhel')) normalizedOS = 'linux';
      else if (os.includes('mac') || os.includes('darwin')) normalizedOS = 'macos';
      else if (os.includes('android')) normalizedOS = 'android';
      else if (os.includes('ios') || os.includes('iphone')) normalizedOS = 'ios';
      
      acc[normalizedOS] = (acc[normalizedOS] || 0) + 1;
      return acc;
    }, {} as { [key: string]: number });

    // Count devices by platform (wazuh, fleet, etc.)
    const platformCounts = devices.reduce((acc, device) => {
      const platform = device.platform || 'unknown';
      acc[platform] = (acc[platform] || 0) + 1;
      return acc;
    }, {} as { [key: string]: number });

    // Get OS icon component
    const getOSIcon = (os: string) => {
      const osLower = os.toLowerCase();
      if (osLower === 'windows') {
        return (
          <svg width="24" height="24" fill="#0078d4" viewBox="0 0 24 24">
            <path d="M0 0h11.377v11.372H0zm12.623 0H24v11.372H12.623zM0 12.628h11.377V24H0zm12.623 0H24V24H12.623z"/>
          </svg>
        );
      } else if (osLower === 'linux') {
        return (
          <svg width="24" height="24" fill="#fcc624" viewBox="0 0 24 24">
            <path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 00-.11.135c-.26.265-.45.46-.636.78-.208.356-.352.782-.437 1.245-.061.331-.096.777-.096 1.312 0 1.623.547 2.74 1.092 3.498.545.758 1.276 1.312 2.286 1.312.356 0 .69-.073 1.004-.2.356-.145.69-.382 1.004-.69.356-.356.69-.802 1.004-1.337.356-.602.69-1.312 1.004-2.123.2-.535.356-1.092.48-1.684.145-.69.247-1.4.315-2.123.08-.823.08-1.684 0-2.545-.08-.823-.17-1.623-.315-2.312-.145-.69-.28-1.312-.48-1.847-.356-1.004-.69-1.847-1.004-2.545-.356-.758-.69-1.312-1.004-1.684-.2-.207-.356-.356-.48-.445a.424.424 0 00-.135-.11c-.356-.186-.69-.287-1.004-.287zm.992 4.176c.58 0 1.051.471 1.051 1.051s-.471 1.051-1.051 1.051-1.051-.471-1.051-1.051.471-1.051 1.051-1.051z"/>
          </svg>
        );
      } else if (osLower === 'macos' || osLower === 'mac') {
        return (
          <svg width="24" height="24" fill="#000000" viewBox="0 0 24 24">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.26-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
          </svg>
        );
      } else if (osLower === 'android') {
        return (
          <svg width="24" height="24" fill="#3ddc84" viewBox="0 0 24 24">
            <path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993.0001.5511-.4482.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.551 0 .9993.4482.9993.9993 0 .5511-.4483.9997-.9993.9997m11.4045-6.02l1.9973-3.4592a.416.416 0 00-.1521-.5676.416.416 0 00-.5676.1521l-2.0223 3.503C17.5902 8.2439 16.8553 7.9988 16.0856 7.9988c-1.3305 0-2.4677.7359-3.074 1.8078l-.52-.8979a.4356.4356 0 00-.6092-.1339c-.26.1508-.3428.4957-.1889.7664l.5149.8902c-.5364.9121-.5364 2.0006 0 2.9128l-.515.8899a.4357.4357 0 00.189.7658.4357.4357 0 00.6092-.1339l.519-.8979c.3408.2058.7284.3369 1.1374.3369.409 0 .7965-.1311 1.1373-.3369l.52.8979a.4356.4356 0 00.6092.1339.4356.4356 0 00.1889-.7658l-.5149-.8902c.6063-1.0719.6063-2.3895 0-3.4614zm-1.015 1.5294c-.2268 0-.4107-.1839-.4107-.4107s.1839-.4107.4107-.4107.4107.1839.4107.4107-.1839.4107-.4107.4107z"/>
          </svg>
        );
      } else if (osLower === 'ios') {
        return (
          <svg width="24" height="24" fill="#000000" viewBox="0 0 24 24">
            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
          </svg>
        );
      } else {
        // Generic device icon for unknown OS
        return (
          <svg width="24" height="24" fill="#4f46e5" viewBox="0 0 24 24">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
            <line x1="8" y1="21" x2="16" y2="21"/>
            <line x1="12" y1="17" x2="12" y2="21"/>
          </svg>
        );
      }
    };

    return (
      <div>
        {/* API Status */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#4f46e5' }}>
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
            <h2 className="card-title" style={{ margin: 0 }}>API Status</h2>
          </div>
          {healthStatus ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#10b981', borderRadius: '50%' }}></div>
              <span style={{ color: '#15803d', fontWeight: '500' }}>Connected to UEM Control API</span>
              <span style={{ color: '#6b7280' }}>v{healthStatus.version}</span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#ef4444', borderRadius: '50%' }}></div>
              <span style={{ color: '#dc2626', fontWeight: '500' }}>API Connection Failed</span>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon blue">
              <svg width="24" height="24" fill="white" viewBox="0 0 24 24">
                <path d="M20 16V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v9m16 0H4m16 0 1.28 2.55A1 1 0 0 1 20.28 20H3.72a1 1 0 0 1-.9-1.45L4 16"/>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Devices</div>
              <div className="stat-value">{totalDevices}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon green">
              <svg width="24" height="24" fill="white" viewBox="0 0 24 24">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <path d="m9 12 2 2 4-4"/>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Compliant</div>
              <div className="stat-value">{compliancePercentage}%</div>
              <div className="stat-sub">{compliantDevices} devices</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon yellow">
              <svg width="24" height="24" fill="white" viewBox="0 0 24 24">
                <circle cx="18" cy="18" r="3"></circle>
                <circle cx="6" cy="6" r="3"></circle>
                <path d="M6 21V9a9 9 0 0 1 9 9"></path>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Policy Drift</div>
              <div className="stat-value">{nonCompliantDevices}</div>
              <div className="stat-sub">Needs remediation</div>
            </div>
          </div>

          <div className="stat-card">
            <div className={`stat-icon ${healthStatus ? 'green' : 'red'}`}>
              <svg width="24" height="24" fill="white" viewBox="0 0 24 24">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">API Status</div>
              <div className="stat-value">{healthStatus ? "Healthy" : "Error"}</div>
              <div className="stat-sub">{healthStatus ? "Connected" : "Disconnected"}</div>
            </div>
          </div>
        </div>

        {/* OS Distribution - Multi-Platform Support */}
        <div className="card">
          <h2 className="card-title" style={{ marginBottom: '16px' }}>Platform Distribution</h2>
          <div className="os-grid">
            {Object.entries(osCounts).map(([os, count]) => (
              <div key={os} className="os-tile">
                <div className="os-icon">
                  {getOSIcon(os)}
                </div>
                <div>
                  <div className="os-label">{os.charAt(0).toUpperCase() + os.slice(1)}</div>
                  <div className="os-count">{count} {count === 1 ? 'device' : 'devices'}</div>
                </div>
              </div>
            ))}
            {Object.keys(osCounts).length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                No devices enrolled yet
              </div>
            )}
          </div>
        </div>

        {/* Platform Breakdown */}
        {Object.keys(platformCounts).length > 0 && (
          <div className="card">
            <h2 className="card-title" style={{ marginBottom: '16px' }}>Management Platform</h2>
            <div className="os-grid">
              {Object.entries(platformCounts).map(([platform, count]) => (
                <div key={platform} className="os-tile">
                  <div className="os-icon">
                    <svg width="24" height="24" fill="#4f46e5" viewBox="0 0 24 24">
                      <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                      <polyline points="2 17 12 22 22 17"/>
                      <polyline points="2 12 12 17 22 12"/>
                    </svg>
                  </div>
                  <div>
                    <div className="os-label">{platform.charAt(0).toUpperCase() + platform.slice(1)}</div>
                    <div className="os-count">{count} {count === 1 ? 'device' : 'devices'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        <div className="activity-grid">
          <div className="activity-card">
            <div className="activity-header">
              <h2 className="activity-title">Recent Activity</h2>
              <span className="badge blue">Live Feed</span>
            </div>
            <div>
              <div className="activity-item">
                <svg className="activity-icon" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#10b981' }}>
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                <div className="activity-text">API connected successfully to UEM Control API</div>
                <div className="activity-time">Just now</div>
              </div>
              <div className="activity-item">
                <svg className="activity-icon" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#3b82f6' }}>
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
                <div className="activity-text">Loaded {totalDevices} devices from database</div>
                <div className="activity-time">Just now</div>
              </div>
              <div className="activity-item">
                <svg className="activity-icon" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#4f46e5' }}>
                  <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                  <polyline points="2 17 12 22 22 17"/>
                  <polyline points="2 12 12 17 22 12"/>
                </svg>
                <div className="activity-text">Compliance data synchronized</div>
                <div className="activity-time">Just now</div>
              </div>
            </div>
          </div>

          <div className="activity-card">
            <h2 className="activity-title" style={{ marginBottom: '16px' }}>Quick Actions</h2>
            <div className="quick-actions">
              <button className="quick-action-btn primary">Push Policy</button>
              <button className="quick-action-btn secondary">Run Script</button>
              <button className="quick-action-btn secondary">Lock/Wipe</button>
              <button className="quick-action-btn secondary">Collect Inventory</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const resolvedTabRaw = resolveTabId(tab);
  const blockedPlatformPage =
    isPlatformNavTab(resolvedTabRaw) &&
    isPlatformStaff &&
    !canAccessPlatformPage(resolvedTabRaw, role);
  const resolvedTab =
    (isPlatformNavTab(resolvedTabRaw) && !isPlatformStaff) ||
    blockedPlatformPage ||
    (resolvedTabRaw === 'client-admin' && !isClientAdmin)
      ? (inCommandCenter || blockedPlatformPage ? 'platform-dashboard' : 'overview')
      : resolvedTabRaw;

  const isPlatformPage = isPlatformNavTab(resolvedTab) || resolvedTab === 'platform-clients';
  const skipDeviceLoadChrome = isPlatformPage || resolvedTab === 'overview';
  const navModuleId = resolveTabId(resolvedTab);
  const moduleKey = NAV_MODULE_BY_ID[navModuleId];
  const hasPlatformModuleAccess =
    inCommandCenter || isPlatformModuleEnabled(navModuleId, access?.enabled_modules);

  if (isLoading) {
    return (
      <div className="app" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="loading">
          <div className="loading-spinner"></div>
          <span style={{ marginLeft: '8px', color: '#6b7280' }}>Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Permission checks fail closed, so without a snapshot the Hub would render
  // as an empty shell with no explanation. Say what happened instead.
  if (!access && accessError && !isPlatformStaff) {
    return (
      <div className="app" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ maxWidth: 420, textAlign: 'center', padding: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: '#0f172a', marginBottom: 8 }}>
            Could not load your permissions
          </h2>
          <p style={{ color: '#64748b', fontSize: 14, marginBottom: 16 }}>
            The Hub cannot show your workspace until it knows what you have access to.
          </p>
          <button
            type="button"
            onClick={() => { void refreshAccess(); }}
            style={{ padding: '8px 16px', borderRadius: 8, background: '#2563eb', color: '#fff', fontWeight: 500 }}
          >
            Retry
          </button>
          <button
            type="button"
            onClick={logout}
            style={{ marginLeft: 8, padding: '8px 16px', borderRadius: 8, background: '#e2e8f0', color: '#0f172a', fontWeight: 500 }}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`app${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
      <UEMSidebar
        tab={tab}
        setTab={setTab}
        selectedOU={selectedOU}
        onSelectOU={setSelectedOU}
        viewingDirectory={viewingDirectory}
        onViewDirectory={setViewingDirectory}
        resolveTabId={resolveTabId}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={toggleSidebarCollapsed}
      />
      <div className="main-content">
        {isPlatformStaff && portalTenantId && (
          <div className="hub-portal-banner">
            <span>
              Viewing client portal: <strong>{portalTenantName || portalTenantId}</strong>
            </span>
            <button
              type="button"
              onClick={() => {
                exitClientPortal();
                setTab('platform-dashboard');
              }}
            >
              Back to Platform
            </button>
          </div>
        )}
        {resolvedTab !== 'overview' && !isPlatformPage && (
          <header className="header">
            <div className="header-left">
              <h1 className="header-title">
                {PAGE_HEADER_TITLES[resolvedTab] ?? UEM_NAV_ITEMS.find((n) => n.id === resolvedTab)?.label ?? PLATFORM_NAV_ITEMS.find((n) => n.id === resolvedTab)?.label ?? resolvedTab}
              </h1>
            </div>
            <div className="header-right">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '0.875rem', color: '#64748b', fontFamily: 'var(--hub-font-body)' }}>{user?.full_name || user?.username}</span>
                <button type="button" className="uem-header-signout" onClick={logout} title="Sign out">Sign out</button>
              </div>
            </div>
          </header>
        )}
        {isPlatformPage && (
          <header className="header">
            <div className="header-left">
              <h1 className="header-title">
                {PLATFORM_NAV_ITEMS.find((n) => n.id === resolvedTab)?.label
                  ?? PAGE_HEADER_TITLES[resolvedTab]
                  ?? resolvedTab}
              </h1>
            </div>
            <div className="header-right">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '0.875rem', color: '#64748b', fontFamily: 'var(--hub-font-body)' }}>{user?.full_name || user?.username}</span>
                <button type="button" className="uem-header-signout" onClick={logout} title="Sign out">Sign out</button>
              </div>
            </div>
          </header>
        )}

        <PageHeaderProvider
          value={
            resolvedTab !== 'overview'
              ? PAGE_HEADER_TITLES[resolvedTab]
                ?? UEM_NAV_ITEMS.find((n) => n.id === resolvedTab)?.label
                ?? PLATFORM_NAV_ITEMS.find((n) => n.id === resolvedTab)?.label
                ?? resolvedTab
              : null
          }
        >
          <main className="content">
            {resolvedTab === 'platform-dashboard' && (
              <PlatformAdminDashboard
                onOpenClient={(id, _name) => {
                  try { sessionStorage.setItem('platform_open_tenant_id', id); } catch { /* ignore */ }
                  setTab('platform-clients');
                }}
              />
            )}
            {(resolvedTab === 'platform-clients' || resolvedTab === 'platform-admin') && (
              <PlatformAdminClients onEnterPortal={openClientPortal} onNavigate={setTab} />
            )}
            {resolvedTab === 'platform-buntu' && <PlatformBuntuBroadcasts />}
            {resolvedTab === 'platform-companion' && <BuntuCompanionHub mode="platform" onNavigate={setTab} />}
            {resolvedTab === 'platform-subscriptions' && <PlatformSubscriptionsPage />}
            {resolvedTab === 'platform-billing' && <PlatformBillingPage />}
            {resolvedTab === 'platform-license' && <PlatformLicenseUsagePage />}
            {resolvedTab === 'platform-entitlements' && <PlatformEntitlementsPage />}
            {resolvedTab === 'platform-software-metering' && <PlatformSoftwareMeteringPage />}
            {resolvedTab === 'platform-users' && <PlatformUsersPage />}
            {resolvedTab === 'platform-audit' && <PlatformAuditLogsPage />}
            {resolvedTab === 'platform-health' && <PlatformSystemHealthPage />}
            {resolvedTab === 'platform-settings' && <PlatformSettingsPage />}

            {resolvedTab === 'overview' && !inCommandCenter && (
              hasPlatformModuleAccess || !moduleKey ? (
                <BuntuDashboard
                  embedded
                  onNavigateToSettings={() => {
                    setSettingsInitialTab('osquery');
                    setTab('settings');
                  }}
                />
              ) : (
                <ModuleNoAccess moduleLabel="Overview" moduleKey={moduleKey} />
              )
            )}
            {!skipDeviceLoadChrome && loading && <LoadingSpinner />}
            {!skipDeviceLoadChrome && !loading && error && <ErrorMessage message={error} onRetry={loadData} />}
            {!inCommandCenter && resolvedTab !== 'overview' && !loading && !error && (
              <>
                {!hasPlatformModuleAccess && moduleKey ? (
                  <ModuleNoAccess
                    moduleLabel={
                      PAGE_HEADER_TITLES[resolvedTab]
                        ?? UEM_NAV_ITEMS.find((n) => n.id === navModuleId)?.label
                        ?? navModuleId
                    }
                    moduleKey={moduleKey}
                  />
                ) : (
                  <>
                    {resolvedTab === 'devices' && <DevicesHub selectedOU={selectedOU} viewingDirectory={viewingDirectory} />}
                    {isPoliciesComplianceTab(resolvedTab) && <PoliciesComplianceHub />}
                    {resolvedTab === 'app-deployment' && <ApplicationDeployment />}
                    {resolvedTab === 'remote-execution' && <RemoteExecutionHub />}
                    {resolvedTab === 'patch-management' && <PatchManagement />}
                    {resolvedTab === 'inventory' && <InventoryHub />}
                    {resolvedTab === 'license-management' && <LicenseManagement />}
                    {resolvedTab === 'asset-management' && <AssetManagementHub />}
                    {resolvedTab === 'software-metering' && <SoftwareMeteringHub />}
                    {resolvedTab === 'analytics' && <AnalyticsHub />}
                    {resolvedTab === 'buntu-companion' && <BuntuCompanionHub mode="tenant" onNavigate={setTab} />}
                    {resolvedTab === 'client-admin' && <ClientAdminConsole />}
                    {resolvedTab === 'user-management' && <UserManagement />}
                    {resolvedTab === 'settings' && (
                      <SettingsHub
                        initialTab={settingsInitialTab}
                        clearInitialTab={() => setSettingsInitialTab(null)}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </main>
        </PageHeaderProvider>
      </div>
      <BuntuCompanion />
    </div>
  );
}

export default App;